import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MemberSignInModal from './MemberSignInModal';
import { useNavigate } from 'react-router-dom';
import { server } from '../../server';

const getBrowserLocationPayload = () => new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.navigator?.geolocation) {
        resolve(null);
        return;
    }

    window.navigator.geolocation.getCurrentPosition(
        (position) => {
            resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                locationSource: 'browser',
            });
        },
        () => resolve(null),
        {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 30000,
        }
    );
});

const resolveMembershipCheckInUrl = async (scannedValue) => {
    const rawValue = String(scannedValue || '').trim();

    if (!rawValue) {
        throw new Error('QR code did not contain a valid value.');
    }

    if (/^BVU-[A-Z0-9-]+$/i.test(rawValue)) {
        return `${server}/membership/scan/${encodeURIComponent(rawValue.toUpperCase())}?mode=checkin`;
    }

    let parsedUrl;
    try {
        parsedUrl = new URL(rawValue, window.location.origin);
    } catch (error) {
        return rawValue;
    }

    const normalizedPathname = parsedUrl.pathname.replace(/\/+$/, '');
    const membershipScanMatch = normalizedPathname.match(/\/api\/v2\/membership\/scan\/([^/]+)$/i);
    if (membershipScanMatch) {
        parsedUrl.searchParams.set('mode', 'checkin');
        return parsedUrl.toString();
    }

    const uniqueIdMatch = normalizedPathname.match(/\/identity\/([^/]+)$/i);
    if (uniqueIdMatch) {
        const uniqueId = decodeURIComponent(uniqueIdMatch[1]).toUpperCase();
        return `${server}/membership/scan/${encodeURIComponent(uniqueId)}?mode=checkin`;
    }

    const handleMatch = normalizedPathname.match(/\/(?:user|profile)\/([^/]+)$/i);
    if (handleMatch) {
        const handle = decodeURIComponent(handleMatch[1]).replace(/^@+/, '');
        const response = await axios.get(`${server}/user/user-info/handle/${encodeURIComponent(handle)}`, {
            withCredentials: true,
        });
        const uniqueId = response.data?.user?.uniqueId;

        if (!uniqueId) {
            throw new Error('Unable to resolve membership identity from the scanned QR code.');
        }

        return `${server}/membership/scan/${encodeURIComponent(uniqueId.toUpperCase())}?mode=checkin`;
    }

    return rawValue;
};

const AdminQrScanner = () => {
    const scanner = useRef(null);
    const videoEl = useRef(null);
    const qrBoxEl = useRef(null);
    const [qrOn, setQrOn] = useState(true);
    const [scannedResult, setScannedResult] = useState('');
    const [signedInMember, setSignedInMember] = useState(null);
    const [action, setAction] = useState('signin');
    const debounceTimeout = useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate(); 

    const onScanSuccess = async (result) => {
        if (isProcessing) return;

        console.log(result);
        setScannedResult(result?.data);
        setIsProcessing(true);

        if (scanner.current) {
            scanner.current.stop();
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(async () => {
            try {
                const requestUrl = await resolveMembershipCheckInUrl(result?.data);
                const engagementContext = await getBrowserLocationPayload();
                const response = await axios.get(requestUrl, {
                    withCredentials: true,
                    params: engagementContext || undefined,
                });
                if (response.status === 200) {
                    const { member, message } = response.data;

                    if (!member || !message) {
                        throw new Error('Scanned QR code did not return membership check-in data.');
                    }

                    toast.success(message);
                    setSignedInMember(member);
                    setAction(member.isSignedIn ? 'signin' : 'signout');
                }
            } catch (error) {
                console.error('Error signing in/out member:', error);
                toast.error('Failed to process member.');
            } finally {
                setIsProcessing(false); 
            }
        }, 500);
    };

    // Handle scan failure
    const onScanFail = (err) => {
        console.log(err);
    };

    // Handle modal close and restart the scanner
    const handleCloseModal = () => {
        setSignedInMember(null);
        if (scanner.current) {
            scanner.current.start(); 
        }
    };

    // Handle stopping the scanner and exiting the scanning process
    const handleExitScanner = () => {
        if (scanner.current) {
            scanner.current.stop(); 
        }
        setQrOn(false); 
        navigate('/admin-studio-dashboard'); 
    };

    useEffect(() => {
        if (videoEl.current && !scanner.current) {
            scanner.current = new QrScanner(videoEl.current, onScanSuccess, {
                onDecodeError: onScanFail,
                preferredCamera: 'environment',
                highlightScanRegion: true,
                highlightCodeOutline: true,
                overlay: qrBoxEl.current || undefined,
            });

            scanner.current.start()
                .then(() => setQrOn(true))
                .catch((err) => {
                    if (err) setQrOn(false);
                });
        }

        return () => {
            if (scanner.current) {
                scanner.current.stop();
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!qrOn) {
            alert("Camera is blocked or not accessible. Please allow camera in your browser permissions and Reload.");
        }
    }, [qrOn]);

    return (
        <div className="qr-reader">
            <button onClick={handleExitScanner} className="close-scanner-button">
                Exit QR Scanner
            </button>
            <video ref={videoEl} style={{ display: qrOn ? 'block' : 'none' }}></video>
            <div ref={qrBoxEl} className="qr-box">
                <img
                    src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1719243841/qr-frame_npsl74.svg"
                    alt="Qr Frame"
                    width={256}
                    height={256}
                    className="qr-frame"
                    style={{ display: qrOn ? 'block' : 'none' }}
                />
            </div>
            
            {signedInMember && (
                <MemberSignInModal 
                    member={signedInMember} 
                    onClose={handleCloseModal}
                    action={action}
                />
            )}
        </div>
    );
};

export default AdminQrScanner;
